import React, { useState } from "react";
import { UserAuth } from "../../context/AuthContext";
import { priceIfIJoin, minSharePrice, sharePrice, formatPieces } from "../../utils/pricing";
import { useNavigate, Link } from "react-router-dom";
import BeatLoader from "react-spinners/BeatLoader";
import { formatCourseDate } from "../../utils/courseTime";
import { useLang } from "../../i18n/LanguageContext";

/**
 * Modal d'inscription à un cours.
 * - Prix partagé : le nouvel inscrit paie prix / (inscrits + 1)
 * - Les élèves déjà inscrits sont remboursés de la différence
 * - Débite le solde de l'élève via joinCourse()
 * - Ferme via onClose()
 */
function JoinCourseCard({ course, onClose }) {
  const { user, joinCourse } = UserAuth();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Acceptation des CGV + demande d'exécution pendant le délai de rétractation (CDE art. VI.53)
  const [agreed, setAgreed] = useState(false);

  const totalCharge = priceIfIJoin(course);
  const nextShare = sharePrice(course.price, (Number(course.students) || 0) + 2);
  const lowest = minSharePrice(course);
  const canDropMore = (Number(course.students) || 0) + 1 < Number(course.maxStudents);
  const balance = Number(user?.balance) || 0;
  const notEnough = user && balance < totalCharge;

  const handleJoin = async () => {
    if (!user) {
      navigate("/signin");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await joinCourse(course);
      navigate("/mes_cours");
    } catch (err) {
      if (err.i18nKey) {
        const vars = {};
        Object.entries(err.i18nAmounts || {}).forEach(([k, v]) => (vars[k] = formatPieces(v, lang)));
        setError(t(err.i18nKey, vars));
      } else {
        setError(t(err.message));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-start justify-center px-4 overflow-y-auto py-8">
      {isLoading ? (
        <div className="flex items-center justify-center my-auto">
          <BeatLoader color="#502887" size={20} />
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 relative my-auto">
          {/* Fermer */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-stone-400 hover:text-stone-700 text-2xl leading-none"
          >
            ×
          </button>

          {/* Titre */}
          <div className="text-center mb-6">
            <div className="text-4xl mb-2">🧙‍♀️</div>
            <h3 className="text-2xl font-bold text-stone-800">{course.name}</h3>
            <p className="text-stone-500 text-sm mt-1">{course.description}</p>
          </div>

          {/* Détails */}
          <div className="bg-creme rounded-xl p-4 space-y-2 text-sm mb-6">
            <div className="flex justify-between">
              <span className="text-stone-500">{t("Date")}</span>
              <span className="font-semibold">{formatCourseDate(course, lang)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-500">{t("Visio")}</span>
              <span className="font-semibold">
                {course.videoType === "custom" ? t("Lien du professeur") : t("Salle Tatie Soso (Jitsi)")}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-500">{t("Places")}</span>
              <span className="font-semibold">{course.students || 0}/{course.maxStudents} {t("élèves")}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-500">{t("Prix du cours")}</span>
              <span className="font-semibold">{formatPieces(course.price, lang)}</span>
            </div>
            <div className="border-t pt-2 flex justify-between font-bold">
              <span>{t("Tu payes maintenant")}</span>
              <span className="text-prune text-base">{formatPieces(totalCharge, lang)}</span>
            </div>
          </div>

          {user && (
            <p className={`text-xs text-center mb-4 ${notEnough ? "text-red-600" : "text-stone-500"}`}>
              {t("Ton solde : {n}", { n: formatPieces(balance, lang) })}
              {notEnough && ` — ${t("rechargez votre portefeuille pour vous inscrire.")}`}
            </p>
          )}

          {/* Erreur */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">
              ⚠️ {error}
            </div>
          )}

          <p className="text-xs text-stone-400 text-center mb-4">
            {canDropMore
              ? t("Le prix est partagé entre les inscrits : si quelqu'un te rejoint, ta part passe à {next} (jusqu'à {min} si le cours est complet) et tu es remboursé·e automatiquement.", { next: formatPieces(nextShare, lang), min: formatPieces(lowest, lang) }) + " "
              : ""}
            {t("Le bouton pour rejoindre la visio apparaîtra dans « Mes cours » 15 minutes avant le début.")}
          </p>

          {user && !notEnough && (
            <label className="flex items-start gap-3 text-xs text-stone-700 bg-creme rounded-xl p-3 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-prune shrink-0"
              />
              <span>
                {t("J'accepte les")}{" "}
                <Link to="/cgv" target="_blank" className="text-prune underline">{t("conditions générales")}</Link>{" "}
                {t("et je demande que le cours ait lieu à la date prévue. Je sais que je perds mon droit de rétractation de 14 jours une fois le cours donné.")}
              </span>
            </label>
          )}

          {notEnough ? (
            <button
              onClick={() => navigate("/portefeuille")}
              className="w-full py-3 bg-or text-prune-deep rounded-full font-bold text-base hover:bg-or-dark transition-colors"
            >
              {t("Recharger mon portefeuille")}
            </button>
          ) : (
            <button
              onClick={handleJoin}
              disabled={user && !agreed}
              className="w-full py-3 bg-prune text-white rounded-full font-bold text-base hover:bg-prune-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {user ? `${t("Confirmer l'inscription")} — ${formatPieces(totalCharge, lang)}` : t("Se connecter pour s'inscrire")}
            </button>
          )}
          <button
            onClick={onClose}
            className="w-full py-2 mt-3 text-stone-500 text-sm hover:text-stone-700 transition-colors"
          >
            {t("Annuler")}
          </button>
        </div>
      )}
    </div>
  );
}

export default JoinCourseCard;
